/**
 * Data Layer — Shared Helpers
 *
 * Internal utilities used by all domain modules.
 * NOT exported to consumers — only used within src/data/domains/*.
 */

import { safeStorageWrite } from '../utils/storageQuota';
import { STORAGE_KEY } from '../config/constants';

// ─── Configuration ────────────────────────────────────────────────────────────

/**
 * De basis-URL van de Laravel-API.
 *
 * ⚠️ NIET VULLEN ZONDER EERST HET DOMEIN AAN TE ZETTEN in `BACKEND_DOMAINS`
 * hieronder. Vullen alleen zet de mógelijkheid aan; welk domein er gebruik van
 * maakt staat in die tabel. Dat onderscheid is met opzet gemaakt (zie daar).
 *
 * Staging: https://meester-djay-api-staging-4ljj0y.laravel.cloud
 */
export const API_BASE = '';

/**
 * ⚠️ AANZETTEN VEREIST EEN WERKENDE AUTH-FLOW.
 *
 * Elke RLS-policy in `supabase/migrations/0001_secure_foundation.sql` bouwt op
 * `auth.uid()`. Zolang de app zijn eigen localStorage-sessiebegrip gebruikt, is
 * `auth.uid()` null en geeft de database niets vrij — de app lijkt dan leeg in
 * plaats van stuk, wat een verwarrende faalmodus is.
 *
 * Inloggen en registreren lopen inmiddels volledig via Supabase Auth, en de
 * migraties zijn uitgevoerd. Wat overblijft is dat de dataLayer per domein moet
 * omgaan — zie `BACKEND_DOMAINS`.
 */
export const USE_SUPABASE = false;

/**
 * Is er überhaupt een backend om naartoe te praten?
 *
 * Dit is NIET de vraag "gaat dit domein naar de server". Dat is
 * `isBackendEnabled(domein)`.
 */
export const BACKEND_AVAILABLE = Boolean(API_BASE) || USE_SUPABASE;

/**
 * ── PER DOMEIN, EN WAAROM DAT GEEN LUXE IS ────────────────────────────────────
 *
 * Hiervoor was dit één vlag voor alle domeinen tegelijk. Dat betekende: op het
 * moment dat `API_BASE` gevuld wordt, slaan ALLE domeinen om — ook de negen die
 * nog `Backend not implemented` gooien. En zo'n storing verschijnt niet als
 * foutmelding maar als een leeg scherm, want de aanroepende schermen vangen een
 * mislukte load af met een placeholder.
 *
 * Dat is precies de faalvorm die dit project keer op keer heeft geraakt: een
 * stap die niets meldt als hij niet gebeurt. Eén vlag omzetten zou een hele laag
 * autorisatie én tien domeinen tegelijk voor het eerst echt laten draaien.
 *
 * Dus: zet hier één domein op `true` zodra zijn `*Backend.ts` bestaat én
 * gecontroleerd is. Nooit meerdere in dezelfde stap — dan weet je bij een
 * storing niet welke het was.
 *
 * De drie die `true` KUNNEN worden zodra de API staat, zijn de gemigreerde
 * domeinen: `assessment` (D3/D4), `practiceAssignments` (D11) en `homework`
 * (D13). De rest heeft nog geen backend-implementatie.
 */
export const BACKEND_DOMAINS = {
  // Gemigreerd — hebben een werkende *Backend.ts
  assessment: false,
  practiceAssignments: false,
  homework: false,
  // Nog geen backend-implementatie; `true` zetten levert een leeg scherm op
  djay: false,
  oefenlab: false,
  preferences: false,
  learnerPrefs: false,
  adaptiveConfig: false,
  governance: false,
  admin: false,
  accountState: false,
  teacherNotes: false
} as const;
export type BackendDomain = keyof typeof BACKEND_DOMAINS;

/**
 * Gaat DIT domein naar de server?
 *
 * Twee voorwaarden, en beide moeten waar zijn: er moet een backend zijn, en dit
 * domein moet er klaar voor zijn verklaard. De eerste is configuratie, de tweede
 * is een besluit.
 */
export function isBackendEnabled(domain: BackendDomain): boolean {
  return BACKEND_AVAILABLE && BACKEND_DOMAINS[domain];
}

/**
 * @deprecated Gebruik `isBackendEnabled('<domein>')`.
 *
 * Blijft bestaan voor `dataLayer.ts`, die alleen rapporteert of er een backend
 * is. Gebruik hem NIET om te beslissen of een leesactie naar de server gaat —
 * dat is per domein verschillend en dat verschil is de hele reden dat deze
 * constante is opgesplitst.
 */
export const USE_BACKEND = BACKEND_AVAILABLE;
export { STORAGE_KEY };

// ─── Internal Helpers ─────────────────────────────────────────────────────────

/** Generic localStorage read with JSON parse */
export function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

/** Generic localStorage write with JSON stringify */
export function writeJson(key: string, value: unknown): boolean {
  return safeStorageWrite(key, JSON.stringify(value));
}

/** Generic localStorage remove */
export function removeKey(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {

    // silent
  }
}

/**
 * Een fout van de API, met de body erbij.
 *
 * De oude `apiFetch` gooide `new Error(status + statusText)` en gooide de body
 * weg. Juist die body bevat `message` en `errors` — precies wat een gebruiker
 * moet lezen. Zonder dat kon de UI alleen "er ging iets mis" zeggen, ook als de
 * server exact wist welk veld fout was.
 *
 * `status` staat er los op omdat 401 iets anders betekent dan 500: het eerste is
 * "je sessie is verlopen, log opnieuw in", het tweede is een storing. Die twee
 * niet kunnen onderscheiden is hoe een defecte server als een lege pagina leest.
 */
export interface ApiFieldRule {
  /** De validatieregel die faalde, bv. `email`, `required`, `min`. */
  rule: string;
  /** Parameters van die regel, bv. `['8']` bij `min`. Altijd strings. */
  params: string[];
}
export class ApiError extends Error {
  readonly status: number;
  /**
   * De ENGELSE teksten van de server, per veld.
   *
   * ⚠️ Hier NIET op matchen. Dit is proza van het framework en het verandert
   * tussen versies. Gebruik `fieldRules` — zie daar.
   *
   * Blijft beschikbaar voor logging: bij een regel die we nog niet kennen is de
   * originele tekst het enige aanknopingspunt.
   */
  readonly fieldErrors: Record<string, string[]>;
  /**
   * De REGEL die faalde, per veld — de stabiele ingang.
   *
   * Wij vertalen validatiefouten zelf, zodat de teksten in één taalbestand staan
   * en niet half in de server. Maar dan kun je niet op de Engelse tekst mappen:
   * die breekt zodra Laravel de formulering aanpast, en je kunt "ongeldig
   * e-mailadres" niet van "te lang" onderscheiden zonder die tekst te lezen.
   *
   * `codes` geeft de regelnaam plus zijn parameters. Daarmee kan de UI zeggen
   * "minimaal 8 tekens" zonder die 8 náást de server te hardcoderen — wat anders
   * precies het soort stille afwijking wordt dat pas opvalt als iemand een
   * wachtwoord van 7 tekens probeert.
   */
  readonly fieldRules: Record<string, ApiFieldRule[]>;
  constructor(status: number, message: string, fieldErrors: Record<string, string[]> = {}, fieldRules: Record<string, ApiFieldRule[]> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.fieldRules = fieldRules;
  }

  /** Sessie verlopen of geen token — de gebruiker moet opnieuw inloggen. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /** Validatiefout: `fieldErrors` bevat per veld de meldingen. */
  get isValidation(): boolean {
    return this.status === 422;
  }
}

/**
 * Waar het API-token staat.
 *
 * Sanctum-tokens zijn bearer-tokens, dus ze moeten ergens bewaard worden.
 * `sessionStorage` en niet `localStorage`: dan overleeft het token het sluiten
 * van het tabblad niet. Op een gezinstablet is dat het verschil tussen "papa is
 * uitgelogd" en "papa's account staat morgen nog open voor wie het ding pakt".
 *
 * ⚠️ Wachtwoord wijzigen levert een NIEUW token op en trekt het oude in. Wie
 * `setAuthToken` daarna niet aanroept, is stil uitgelogd — de volgende
 * aanroep geeft 401 zonder dat er iets zichtbaar gebeurde.
 */
const AUTH_TOKEN_KEY = 'djay_api_token';
export function setAuthToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(AUTH_TOKEN_KEY, token);else sessionStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // Private mode kan schrijven weigeren. Dan werkt de sessie alleen zolang
    // dit tabblad open staat, en dat is beter dan een harde crash.
  }
}
export function getAuthToken(): string | null {
  try {
    return sessionStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Aanroep naar de Laravel-API.
 *
 * DRIE DINGEN DIE HIER BEWUST STAAN
 *
 * 1. `Accept: application/json` is niet optioneel. Zonder die header stuurt
 *    Laravel een niet-ingelogde aanroep naar een inlogPAGINA die in een API niet
 *    bestaat, en dat komt terug als 500 in plaats van 401. Dan is een verlopen
 *    sessie niet van een defecte server te onderscheiden.
 *
 * 2. De body wordt bij een fout GELEZEN en niet weggegooid. Zie `ApiError`.
 *
 * 3. `headers` staat ná de spread van `options`, zodat een aanroeper zijn eigen
 *    headers kan meegeven zonder deze twee per ongeluk te wissen. Stond het
 *    ervoor, dan verdween `Authorization` bij elke aanroep die iets aan de
 *    headers toevoegde — stil, en alleen zichtbaar als een 401.
 */
export async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? {
        Authorization: `Bearer ${token}`
      } : {}),
      ...(options.headers ?? {})
    }
  });
  if (!response.ok) {
    // De body kan ontbreken (204, of een proxy die HTML teruggeeft). Dan valt
    // het terug op de status — maar nooit stil: er komt altijd een ApiError.
    let message = `API-fout ${response.status}`;
    let fieldErrors: Record<string, string[]> = {};
    let fieldRules: Record<string, ApiFieldRule[]> = {};
    try {
      const body = await response.json();
      if (typeof body?.message === 'string' && body.message) message = body.message;
      if (body?.errors && typeof body.errors === 'object') fieldErrors = body.errors;
      // `codes` is de stabiele ingang; ontbreekt hij (oudere API-versie), dan
      // valt de UI terug op een algemene tekst per veld in plaats van op de
      // Engelse proza uit `errors`.
      if (body?.codes && typeof body.codes === 'object') fieldRules = body.codes;
    } catch {
      // Geen JSON-body. `message` blijft de status.
    }
    throw new ApiError(response.status, message, fieldErrors, fieldRules);
  }

  // 204 No Content heeft geen body; `.json()` zou daarop gooien.
  if (response.status === 204) return undefined as T;
  return response.json();
}
